import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { useAction } from "./fields.js";
import { useProviderStatus } from "./providers.js";
import type { ColoringJob, ColoringPage, ColoringVariant, PageOp, QualityCheck } from "./types.js";

/** The open (queued/running) job for a target, and the latest failure, from workspace state. */
export function usePageJobs(targetId: string): { open: ColoringJob | null; error: ColoringJob | null } {
  const { state } = useColoring();
  const mine = state.jobs.filter((j) => j.pageId === targetId);
  const open = mine.find((j) => j.status === "queued" || j.status === "generating") ?? null;
  const last = mine[mine.length - 1];
  return { open, error: !open && last?.status === "error" ? last : null };
}

/** Large preview of the page's current artwork, with generation status overlaid. */
export function PagePreview({ page }: { page: ColoringPage }) {
  const { projectId, state } = useColoring();
  const { trimWidth, trimHeight } = state.book;
  const { open } = usePageJobs(page.id);
  return (
    <div className="page-preview-stage">
      <div className="page-preview-sheet" style={{ aspectRatio: `${trimWidth} / ${trimHeight}` }}>
        {page.currentVariantId ? (
          <img src={coloringApi.imageUrl(projectId, page.currentVariantId)} alt={page.title} />
        ) : (
          <div className="page-preview-empty">
            <Icon name="image" size={34} />
            <span>No artwork yet</span>
          </div>
        )}
        {open && (
          <div className="page-preview-busy">
            <Icon name="clock" size={22} className="spin" />
            <span>{open.status === "queued" ? "Waiting in the queue…" : "Generating…"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const FIXABLE = new Set(["color", "gray", "background", "margins"]);

/** Generation, refinement, versions and quality for one page. */
export function PageGenerationControls({ page }: { page: ColoringPage }) {
  const { projectId, state, reload } = useColoring();
  const toast = useToast();
  const { busy, run } = useAction();
  const providers = useProviderStatus();
  const { open, error } = usePageJobs(page.id);
  const [instruction, setInstruction] = useState("");
  const [sceneOpen, setSceneOpen] = useState(false);
  const [scene, setScene] = useState(page.scene);
  const [variants, setVariants] = useState<ColoringVariant[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const hasArt = !!page.currentVariantId;
  const working = !!open || busy !== null;
  const noProvider = providers !== null && !providers.active;

  const loadVariants = useCallback(async () => {
    try {
      setVariants((await coloringApi.pageVariants(projectId, page.id)).variants);
    } catch {
      /* the list is a convenience; ignore failures */
    }
  }, [projectId, page.id]);
  useEffect(() => {
    void loadVariants();
  }, [loadVariants, page.currentVariantId, state.jobs.length]);
  useEffect(() => setScene(page.scene), [page.scene]);

  const queue = (op: PageOp, text?: string, success?: string) =>
    run(op, async () => {
      await coloringApi.generatePage(projectId, page.id, op, text);
      await reload();
    }, success);

  const current = variants.find((v) => v.id === page.currentVariantId) ?? null;
  const report = page.quality && page.quality.variantId === page.currentVariantId ? page.quality : null;
  const fixable = !!report?.checks.some((c) => c.status === "warn" && FIXABLE.has(c.id));

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("That image is larger than 25 MB.");
      return;
    }
    await run("upload", async () => {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error("Could not read that file."));
        r.readAsDataURL(file);
      });
      await coloringApi.uploadPageImage(projectId, page.id, dataUrl);
      await reload();
    }, "Your artwork was added as this page's current version.");
  };

  return (
    <>
      {noProvider && (
        <div className="gen-note warn">
          <Icon name="alert" size={14} />
          <span>
            No image provider is connected yet, so artwork can't be generated. {providers?.providers.find((p) => !p.configured)?.hint} You can still upload your own artwork.
          </span>
        </div>
      )}

      <Section title="Generate" icon="sparkles">
        <div className="gen-actions">
          <button className="btn btn-primary" disabled={working || noProvider} onClick={() => void queue("generate", undefined, hasArt ? "Queued a new version." : "Queued for generation.")}>
            <Icon name={open ? "clock" : "sparkles"} size={15} className={open ? "spin" : ""} /> {hasArt ? "Regenerate" : "Generate"}
          </button>
          <button className="btn" disabled={working || noProvider || !hasArt} onClick={() => void queue("variant", undefined, "Queued a variant.")} title="A different take with the same subject and style">
            Create Variant
          </button>
        </div>
        {open && <p className="faint gen-status"><Icon name="clock" size={12} className="spin" /> {open.status === "queued" ? "Waiting in the queue…" : "Generating - this can take a minute."}</p>}
        {error && (
          <div className="gen-note err">
            <Icon name="alert" size={14} />
            <span>{error.error ?? "Generation failed."}</span>
            <button className="btn btn-sm btn-ghost" disabled={working || noProvider} onClick={() => void queue((error.kind as PageOp) || "generate", error.instruction ?? undefined)}>Retry</button>
          </div>
        )}
        {providers?.active && (
          <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Using {providers.active.label} ({providers.active.model}). {state.book.consistency} consistency.</p>
        )}
      </Section>

      <Section title="Refine" icon="edit">
        <label className="label">Describe a change</label>
        <textarea
          className="textarea"
          rows={3}
          value={instruction}
          placeholder={'e.g. "Make the flowers larger." or "Remove the lamp." or "Give Boo a coffee cup."'}
          onChange={(e) => setInstruction(e.target.value)}
        />
        <button
          className="btn btn-sm btn-primary"
          style={{ marginTop: 8 }}
          disabled={working || noProvider || !instruction.trim()}
          onClick={async () => {
            await queue("revise", instruction.trim(), "Queued your change.");
            setInstruction("");
          }}
        >
          Apply change
        </button>
        {hasArt && providers?.active && !providers.active.capabilities.imageEdit && (
          <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>
            {providers.active.label} can't edit an image directly, so the page is re-drawn using the current artwork as a guide.
          </p>
        )}
        <div className="gen-chips">
          <button className="btn btn-sm" disabled={working || noProvider || !hasArt} onClick={() => void queue("simplify", undefined, "Queued: simplify.")}>Simplify</button>
          <button className="btn btn-sm" disabled={working || noProvider || !hasArt} onClick={() => void queue("add-detail", undefined, "Queued: add detail.")}>Add Detail</button>
          <button className="btn btn-sm" disabled={working || noProvider || !hasArt} onClick={() => void queue("remove-detail", undefined, "Queued: remove detail.")}>Remove Detail</button>
          <button className="btn btn-sm" disabled={working || !hasArt} title="Snap grays and tint to clean black line art (no image credits used)" onClick={() => void queue("improve-lineart", undefined, "Line art cleaned up.")}>Improve Line Art</button>
          <button className="btn btn-sm" disabled={working} onClick={() => setSceneOpen((o) => !o)}>Change Scene</button>
        </div>
        {sceneOpen && (
          <div style={{ marginTop: 10 }}>
            <label className="label">New scene</label>
            <textarea className="textarea" rows={4} value={scene} onChange={(e) => setScene(e.target.value)} />
            <button
              className="btn btn-sm btn-primary"
              style={{ marginTop: 8 }}
              disabled={working || noProvider || !scene.trim()}
              onClick={async () => {
                await run("scene", async () => {
                  await coloringApi.updatePage(projectId, page.id, { scene: scene.trim() });
                  await coloringApi.generatePage(projectId, page.id, "generate");
                  await reload();
                }, "Scene updated - generating a new page.");
                setSceneOpen(false);
              }}
            >
              Save &amp; regenerate
            </button>
          </div>
        )}
      </Section>

      <Section title="Approve" icon="check">
        <div className="gen-actions">
          <button
            className={`btn ${page.status === "approved" ? "" : "btn-primary"}`}
            disabled={working || !hasArt}
            onClick={() => void run("approve", async () => {
              await coloringApi.approvePage(projectId, page.id, page.status !== "approved");
              await reload();
            })}
          >
            <Icon name="check" size={15} /> {page.status === "approved" ? "Approved - undo" : "Approve this version"}
          </button>
          <button className="btn" disabled={working} onClick={() => fileInput.current?.click()}>
            Upload artwork
          </button>
          <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </Section>

      <Section title="Quality check" icon="target">
        <button className="btn btn-sm" disabled={working || !hasArt} onClick={() => void run("quality", async () => { await coloringApi.checkQuality(projectId, page.id); await reload(); })}>
          <Icon name={busy === "quality" ? "clock" : "target"} size={14} className={busy === "quality" ? "spin" : ""} /> {report ? "Re-run check" : "Run quality check"}
        </button>
        {page.quality && !report && <p className="faint" style={{ fontSize: 11.5 }}>The artwork changed since the last check.</p>}
        {report && (
          <>
            <ul className="quality-list">
              {report.checks.map((c) => <QualityRow key={c.id} check={c} />)}
            </ul>
            {fixable && (
              <button className="btn btn-sm btn-primary" disabled={working} onClick={() => void run("fix", async () => {
                const res = await coloringApi.fixQuality(projectId, page.id);
                await reload();
                toast.success(res.fixed.join(" "));
              })}>
                Fix automatically
              </button>
            )}
            <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Fixing makes a new version; your current one is kept in Versions.</p>
          </>
        )}
      </Section>

      <Section title={`Versions (${variants.length})`} icon="layers" defaultOpen={false}>
        {variants.length === 0 ? (
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>No versions yet.</p>
        ) : (
          <div className="variant-strip">
            {variants.map((v) => (
              <button
                key={v.id}
                className={`variant-thumb ${v.id === page.currentVariantId ? "on" : ""}`}
                title={`${v.source === "uploaded" ? "Uploaded" : v.settings.op ?? "generated"} - ${new Date(v.createdAt).toLocaleString()}${v.id === page.currentVariantId ? " (current)" : " - click to use"}`}
                disabled={working || v.id === page.currentVariantId}
                onClick={() => void run("use", async () => { await coloringApi.useVariant(projectId, page.id, v.id); await reload(); }, "Switched to that version.")}
              >
                <img src={coloringApi.imageUrl(projectId, v.id)} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
        {current && (
          <div className="variant-meta">
            <div><strong>{current.source === "uploaded" ? "Uploaded artwork" : current.provider ? `${current.provider}${current.model ? ` · ${current.model}` : ""}` : "Generated"}</strong></div>
            {current.seed !== null && <div className="faint">Seed {current.seed}</div>}
            {current.refs.length > 0 && <div className="faint">{current.refs.length} reference image{current.refs.length === 1 ? "" : "s"} used</div>}
            {(current.settings.notes ?? []).map((n, i) => <div key={i} className="faint">• {n}</div>)}
          </div>
        )}
      </Section>
    </>
  );
}

function QualityRow({ check }: { check: QualityCheck }) {
  const icon: IconName = check.status === "pass" ? "check" : check.status === "warn" ? "alert" : "info";
  return (
    <li className={`quality-row q-${check.status}`}>
      <Icon name={icon} size={13} />
      <div>
        <div>{check.label}</div>
        {check.detail && <div className="faint" style={{ fontSize: 11.5 }}>{check.detail}</div>}
      </div>
    </li>
  );
}

function Section({ title, icon, children, defaultOpen = true }: { title: string; icon: IconName; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="gen-section">
      <button className="gen-section-head" onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={14} />
        <Icon name={icon} size={14} style={{ color: "var(--accent)" }} />
        <span>{title}</span>
      </button>
      {open && <div className="gen-section-body">{children}</div>}
    </div>
  );
}
